import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

/**
 * Generate a PDF report of sales coverage data, opened in a new tab.
 */
export function generateCoveragePdfReport(coverageData) {
  if (!coverageData || !coverageData.days?.length) {
    return { error: "No coverage data to export." };
  }

  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 14;
  let y = margin;

  // --- Header ---
  doc.setFontSize(10);
  doc.setFont("helvetica", "bold");
  doc.text("Pizza Felice LLC", margin, y);
  doc.text("Sales Coverage Report", pageWidth / 2, y, { align: "center" });
  doc.setFont("helvetica", "normal");
  doc.text("Internal (do not circulate)", pageWidth - margin, y, { align: "right" });
  y += 6;

  // --- Date range subtitle ---
  const firstDate = formatDate(coverageData.days[0].date);
  const lastDate = formatDate(coverageData.days[coverageData.days.length - 1].date);
  doc.setFontSize(9);
  doc.setTextColor(100, 100, 100);
  doc.text(`${firstDate} – ${lastDate}`, pageWidth / 2, y, { align: "center" });
  doc.setTextColor(0, 0, 0);
  y += 8;

  // --- Summary ---
  const s = coverageData.summary;
  doc.setFontSize(10);
  doc.setFont("helvetica", "bold");
  doc.text("Summary", margin, y);
  y += 5;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.text(`Total Days: ${s.totalDays}    Complete: ${s.completeDays}    Incomplete: ${s.incompleteDays}    Avg Coverage: ${s.averageCoverage}%`, margin, y);
  y += 8;

  // --- Daily Coverage Table ---
  const dailyRows = coverageData.days.map((d) => [
    formatDate(d.date),
    d.dayOfWeek,
    d.isComplete ? "Complete" : d.reported === 0 ? "No data" : "Incomplete",
    `${d.reported} / ${d.expected}`,
    `${d.percentage}%`,
    d.missing > 0 ? `${d.missing} missing` : "-",
  ]);

  autoTable(doc, {
    startY: y,
    margin: { left: margin, right: margin },
    head: [["Date", "Day", "Status", "Reported / Expected", "Coverage %", "Missing"]],
    body: dailyRows,
    theme: "grid",
    styles: { fontSize: 8, cellPadding: 2 },
    headStyles: {
      fillColor: [63, 81, 181],
      textColor: [255, 255, 255],
      fontStyle: "bold",
      fontSize: 8,
    },
    bodyStyles: { fontSize: 8 },
    didParseCell(data) {
      if (data.section === "body" && data.column.index === 2) {
        const val = data.cell.raw;
        if (val === "Complete") data.cell.styles.textColor = [76, 175, 80];
        else if (val === "Incomplete") data.cell.styles.textColor = [255, 152, 0];
        else data.cell.styles.textColor = [158, 158, 158];
      }
    },
  });

  y = doc.lastAutoTable.finalY + 10;

  // --- Missing Combinations per incomplete day ---
  const incompleteDays = coverageData.days.filter((d) => d.missing > 0 && d.reported > 0 && d.missingCombinations?.length);

  if (incompleteDays.length > 0) {
    // Check if we need a new page
    if (y > pageHeight - 30) {
      doc.addPage();
      y = margin;
    }

    doc.setFontSize(12);
    doc.setFont("helvetica", "bold");
    doc.text("Missing Combinations", margin, y);
    y += 7;

    for (const day of incompleteDays) {
      if (y > pageHeight - 25) {
        doc.addPage();
        y = margin;
      }

      // Get missing items from allCombinations or missingCombinations
      const missing = day.allCombinations
        ? day.allCombinations.filter((c) => c.isMissing)
        : day.missingCombinations;

      doc.setFontSize(9);
      doc.setFont("helvetica", "bold");
      doc.text(`${formatDate(day.date)} (${day.dayOfWeek}) — ${missing.length} missing`, margin, y);
      y += 1;

      const rows = missing.map((c) => [c.platform, c.city, c.location, c.brand]);

      autoTable(doc, {
        startY: y,
        margin: { left: margin, right: margin },
        head: [["Platform", "City", "Location", "Brand"]],
        body: rows,
        theme: "grid",
        styles: { fontSize: 7, cellPadding: 1.5 },
        headStyles: {
          fillColor: [97, 97, 97],
          textColor: [255, 255, 255],
          fontStyle: "bold",
          fontSize: 7,
        },
      });

      y = doc.lastAutoTable.finalY + 6;
    }
  }

  // --- Footer (all pages) ---
  const totalPages = doc.internal.getNumberOfPages();
  const now = new Date();
  const dateStr = now.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  const timeStr = now.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });

  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(130, 130, 130);
    doc.setFont("helvetica", "normal");
    doc.text(`Generated on ${dateStr} at ${timeStr}`, margin, pageHeight - 6);
    doc.text(`Page ${i} / ${totalPages}`, pageWidth - margin, pageHeight - 6, { align: "right" });
  }

  // Open in new tab
  doc.setTextColor(0, 0, 0);
  const blob = doc.output("blob");
  const url = URL.createObjectURL(blob);
  window.open(url, "_blank");

  return { success: true };
}

function formatDate(dateStr) {
  if (!dateStr) return "-";
  const date = new Date(dateStr + "T00:00:00");
  return date.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}
